import { useEffect, useRef } from 'react';
import { getServerNameCode } from '../../config/gcs/gcsConfigs';
import { en } from '../../locales/en';
import { useGcsStore } from '../../store/useGcsStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { selectUnitSystemOrUndefined } from '../../store/selectors/selectUnitSystem';
import type { GcsStore } from '../../store/useGcsStore';
import { InAppNotificationType } from '../../types/ui/inAppNotification';
import type { CenterUnitSystem } from '../../types/gcs/center';

type CenterUnitSystems = Map<string, CenterUnitSystem>;

interface UnitSystemMismatchSummary {
  readonly isInconsistent: boolean;
  readonly body: string;
}

const CENTER_UNIT_SYSTEM_EVALUATION_TIMEOUT_MS = 3000;

const formatCenterList = (centers: readonly string[]): string => `[${centers.join(', ')}]`;

const summarizeUnitSystemMismatch = (centerUnitSystems: CenterUnitSystems): UnitSystemMismatchSummary => {
  if (centerUnitSystems.size === 0) {
    return { isInconsistent: false, body: '' };
  }

  const distinctUnitSystems = new Set(centerUnitSystems.values());

  const hasMismatch = distinctUnitSystems.size > 1;

  if (!hasMismatch) {
    return { isInconsistent: false, body: '' };
  }

  const centersByUnitSystem = Array.from(centerUnitSystems.entries()).reduce<Record<CenterUnitSystem, string[]>>(
    (groups, [gcsId, unitSystem]) => {
      groups[unitSystem].push(getServerNameCode(gcsId));
      return groups;
    },
    {
      imperial: [],
      metric: []
    }
  );

  const imperialCenters = formatCenterList(centersByUnitSystem.imperial);
  const metricCenters = formatCenterList(centersByUnitSystem.metric);

  return {
    isInconsistent: true,
    body: en.inAppNotifications.centerUnitSystemsInconsistentBody
      .replace('{imperialCenters}', imperialCenters)
      .replace('{metricCenters}', metricCenters)
  };
};

export function GcsUnitSystemMismatchListener() {
  const latestCenterUnitSystemsRef = useRef<CenterUnitSystems>(new Map());
  const evaluationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasEvaluatedRef = useRef(false);
  const lastShownBodyRef = useRef<string | null>(null);

  useEffect(() => {
    const selectCenterUnitSystems = (state: GcsStore): CenterUnitSystems => {
      const nextCenterUnitSystems: CenterUnitSystems = new Map();

      state.data.forEach((_, gcsId) => {
        const unitSystem = selectUnitSystemOrUndefined(state.data, gcsId);
        if (unitSystem !== undefined) {
          nextCenterUnitSystems.set(gcsId, unitSystem);
        }
      });

      return nextCenterUnitSystems;
    };

    const equalityFn = (a: CenterUnitSystems, b: CenterUnitSystems): boolean => {
      if (a.size !== b.size) return false;

      for (const [key, unitSystem] of a) {
        if (b.get(key) !== unitSystem) return false;
      }

      return true;
    };

    const evaluateMismatch = (centerUnitSystems: CenterUnitSystems) => {
      const nextSummary = summarizeUnitSystemMismatch(centerUnitSystems);

      if (!nextSummary.isInconsistent) {
        lastShownBodyRef.current = null;
        return;
      }

      if (lastShownBodyRef.current === nextSummary.body) {
        return;
      }

      lastShownBodyRef.current = nextSummary.body;

      useNotificationStore.getState().showNotification({
        type: InAppNotificationType.WARNING,
        title: en.inAppNotifications.centerUnitSystemsInconsistentTitle,
        body: nextSummary.body
      });
    };

    evaluationTimeoutRef.current = setTimeout(() => {
      evaluationTimeoutRef.current = null;
      hasEvaluatedRef.current = true;
      evaluateMismatch(latestCenterUnitSystemsRef.current);
    }, CENTER_UNIT_SYSTEM_EVALUATION_TIMEOUT_MS);

    const unsubscribe = useGcsStore.subscribe(
      selectCenterUnitSystems,
      (nextCenterUnitSystems) => {
        latestCenterUnitSystemsRef.current = nextCenterUnitSystems;

        if (hasEvaluatedRef.current) {
          evaluateMismatch(nextCenterUnitSystems);
        }
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return () => {
      if (evaluationTimeoutRef.current !== null) {
        clearTimeout(evaluationTimeoutRef.current);
        evaluationTimeoutRef.current = null;
      }

      unsubscribe();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (evaluationTimeoutRef.current !== null) {
        clearTimeout(evaluationTimeoutRef.current);
        evaluationTimeoutRef.current = null;
      }
    };
  }, []);

  return null;
}
